import React, { useCallback, useMemo, useState } from "react";
import { ChevronDown } from "../icons/ChevronDown";
import {
  DropdownContainer,
  DropdownItemButton,
  DropdownItemChildren,
  DropdownItemContent,
  DropdownItemDetails,
  DropdownItemHeader,
  DropdownItemName,
  DropdownItemPhone,
  DropdownItemChevron,
  DropdownList,
} from "./HierarchicalDropdown.styles";

export interface HierarchicalNode {
  id: string;
  name: string;
  details?: string | null;
  children?: HierarchicalNode[];
  meta?: unknown;
}

export interface HierarchicalDropdownProps {
  items: HierarchicalNode[];
  primaryColor?: string;
  className?: string;
  leafIcon?: React.ReactNode;
  onLeafClick?: (node: HierarchicalNode) => void;
  isEditor?: boolean;
}

const TEXT_SECONDARY_COLOR = "var(--text-body, #475569)";

const computeMaxDepth = (nodes: HierarchicalNode[], depth = 1): number => {
  let max = depth;
  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      max = Math.max(max, computeMaxDepth(node.children, depth + 1));
    }
  }
  return max;
};

export const HierarchicalDropdown: React.FC<HierarchicalDropdownProps> = ({
  items,
  primaryColor,
  className,
  leafIcon,
  onLeafClick,
  isEditor = false,
}) => {
  const componentClassName = className ?? "palette1";
  const [openPath, setOpenPath] = useState<string[]>([]);

  const maxDepth = useMemo(() => computeMaxDepth(items), [items]);
  const hasNestedLevels = maxDepth > 1;
  const highlightColor = primaryColor ?? "var(--general-primary)";

  const toggleItem = useCallback((id: string, depth: number) => {
    setOpenPath((prev) => {
      const currentOpenAtDepth = prev[depth - 1];
      const next = prev.slice(0, depth - 1);

      if (currentOpenAtDepth === id) {
        return next;
      }

      next[depth - 1] = id;
      return next;
    });
  }, []);

  const renderNodes = (nodes: HierarchicalNode[], depth = 1): React.ReactNode =>
    nodes.map((node) => {
      const hasChildren =
        Array.isArray(node.children) && node.children.length > 0;
      const isLeaf = !hasChildren;
      const isOpen = openPath[depth - 1] === node.id;
      const isRootLevel = depth === 1;
      const shouldHighlight = isRootLevel && hasNestedLevels && isOpen;
      const chevronColor = shouldHighlight ? "#ffffff" : TEXT_SECONDARY_COLOR;

      return (
        <DropdownList key={node.id}>
          <DropdownItemButton
            type="button"
            $depth={depth}
            $hasChildren={hasChildren}
            $highlightColor={highlightColor}
            $applyHighlight={shouldHighlight}
            onClick={() => {
              if (hasChildren) {
                toggleItem(node.id, depth);
                return;
              }

              if (!isEditor) {
                onLeafClick?.(node);
              }
            }}
          >
            <DropdownItemContent>
              <DropdownItemHeader $hasChildren={hasChildren}>
                <DropdownItemName
                  $highlight={shouldHighlight}
                  $isLeaf={isLeaf}
                  $depth={depth}
                >
                  {node.name}
                </DropdownItemName>
                {hasChildren && (
                  <DropdownItemChevron $isOpen={isOpen}>
                    <ChevronDown color={chevronColor} />
                  </DropdownItemChevron>
                )}
              </DropdownItemHeader>
              {isLeaf && node.details && (
                <DropdownItemDetails $isLeaf={isLeaf} $highlight={shouldHighlight}>
                  <DropdownItemPhone>{node.details}</DropdownItemPhone>
                  {leafIcon}
                </DropdownItemDetails>
              )}
            </DropdownItemContent>
          </DropdownItemButton>

          {hasChildren && isOpen && Array.isArray(node.children) && (
            <DropdownItemChildren>
              {renderNodes(node.children, depth + 1)}
            </DropdownItemChildren>
          )}
        </DropdownList>
      );
    });

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <DropdownContainer className={componentClassName}>
      {renderNodes(items)}
    </DropdownContainer>
  );
};
